import { APP_BASE_HREF, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UrlSerializer } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { ToastContainerDirective, ToastrModule } from 'ngx-toastr';

import { AnalyticsModule } from '@app/modules/analytics/analytics.module';
import { GlobalErrorHandler } from '@core/global-error-handler';
import { authTokenInterceptor } from '@core/interceptors/auth-token.interceptor';
import { duplicateRequestInterceptor } from '@core/interceptors/duplicate-request.interceptor';
import { parseErrorInterceptor } from '@core/interceptors/parse-error.interceptor';
import { viewReportTokenInterceptor } from '@core/interceptors/view-report-token.interceptor';
import { AnalyticsDashboardPropertiesState } from '@core/ngxs/state/analytics-dashboard-properties.state';
import { AnalyticsEntitiesPropertiesState } from '@core/ngxs/state/analytics-entities-properties.state';
import { AnalyticsGeneralPropertiesState } from '@core/ngxs/state/analytics-general-properties.state';
import { AnalyticsInsightsPropertiesState } from '@core/ngxs/state/analytics-insights-properties.state';
import { AnalyticsReportPropertiesState } from '@core/ngxs/state/analytics-report-properties.state';
import { AnalyticsSearchPropertiesState } from '@core/ngxs/state/analytics-search-properties.state';
import { AnalyticsUserSettingPropertiesState } from '@core/ngxs/state/analytics-user-setting-properties.state';
import { LIMITS_MESSAGE_CONFIG_PROVIDER } from '@main/services/limits-message-config.provider';
import { LayoutConfigService, downloadServices } from '@shared/services';
import { UiHelperService } from '@shared/services/layout/ui-helper.service';
import { WINDOW_PROVIDERS } from '@shared/services/window.providers';
import { AppComponent } from 'app/app.component';

import { AppConfig } from './app.config';
import { AppRoutingModule } from './app.routes';
import { IntegrationsModule } from './integrations/integrations.module';
import { environment } from '../environments/environment';
import { pipeProviders } from './shared/pipes';
import { AdcToastService } from './shared/ui-kit/adc-toast/adc-toast-service';
import CustomUrlSerializer from './util/custom-url-serializer';

export const initializeApp = (appConfig: AppConfig) => () => appConfig.resolve();

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        IntegrationsModule,
        ToastrModule.forRoot(),
        MatMomentDateModule,
        AnalyticsModule.forRoot({ tool: 'mixpanel' }),
        TranslateModule.forRoot({ defaultLanguage: 'en' }),
        NgxsReduxDevtoolsPluginModule.forRoot({
            disabled: environment.production, // disable in prod
        }),
        ToastContainerDirective,
    ],
    providers: [
        {
            provide: LocationStrategy,
            useClass: PathLocationStrategy, // HashLocationStrategy
        },
        { provide: APP_BASE_HREF, useValue: '/' },
        WINDOW_PROVIDERS,
        LIMITS_MESSAGE_CONFIG_PROVIDER,
        LayoutConfigService,
        AdcToastService,
        provideAppInitializer(() => {
            const initializerFn = initializeApp(inject(AppConfig));
            return initializerFn();
        }),
        {
            provide: UrlSerializer,
            useClass: CustomUrlSerializer,
        },
        {
            provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
            useValue: {
                showDelay: 300,
                disableTooltipInteractivity: true,
            },
        },
        ...downloadServices,
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        provideHttpClient(
            withInterceptors([
                parseErrorInterceptor,
                authTokenInterceptor,
                viewReportTokenInterceptor,
                duplicateRequestInterceptor,
            ]),
        ),
        UiHelperService,
        provideStore([
            AnalyticsGeneralPropertiesState,
            AnalyticsUserSettingPropertiesState,
            AnalyticsSearchPropertiesState,
            AnalyticsDashboardPropertiesState,
            AnalyticsReportPropertiesState,
            AnalyticsEntitiesPropertiesState,
            AnalyticsInsightsPropertiesState,
        ]),
        ...pipeProviders,
    ],
})
export class AppModule {}
